import classNames from 'classnames';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import styles from './Button.module.css';

type ButtonColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'lightBlue'
  | 'destructive';
export type ButtonVariant = 'default' | 'outlined' | 'ghost';
type ButtonSize = 'lg' | 'md' | 'sm' | 'xs';
type ButtonShape = 'squared' | 'rounded';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: ButtonColor;
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

export const Button = ({
  children,
  color = 'default',
  variant = 'default',
  size = 'md',
  shape = 'squared',
  leftIcon,
  rightIcon,
  fullWidth = false,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  type = 'button',
  ...rest
}: ButtonProps) => {
  const isIconOnly = (leftIcon || rightIcon) && !children;
  const resolvedAriaLabel =
    ariaLabel ??
    (isIconOnly && typeof children === 'string' ? children : undefined);

  if (isIconOnly && !resolvedAriaLabel && !ariaLabelledBy) {
    console.warn(
      'Icon-only Button requires aria-label or aria-labelledby for accessibility.'
    );
  }

  return (
    <button
      className={classNames(
        styles.button,
        styles[color],
        styles[variant],
        styles[size],
        styles[shape],
        fullWidth && styles.fullWidth,
        className
      )}
      aria-label={resolvedAriaLabel}
      aria-labelledby={ariaLabelledBy}
      type={type}
      {...rest}
    >
      {leftIcon && <span aria-hidden="true">{leftIcon}</span>}
      {children && <span>{children}</span>}
      {rightIcon && <span aria-hidden="true">{rightIcon}</span>}
    </button>
  );
};
